import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../lib/i18n";

export default function AiChat() {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const listRef = useRef(null);

  useEffect(() => {
    listRef.current?.scrollTo(0, listRef.current.scrollHeight);
  }, [messages, busy]);

  const send = async (e) => {
    e.preventDefault();
    const q = input.trim();
    if (!q || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }]);
    setBusy(true);
    try {
      const { data } = await api.post("/ai/chat", { question: q, lang });
      setMessages((m) => [...m, { role: "ai", text: data.answer }]);
    } catch {
      setMessages((m) => [
        ...m,
        { role: "ai", text: lang === "hi" ? "माफ़ कीजिए, अभी जवाब नहीं दे पा रहा। थोड़ी देर में कोशिश करें।" : "Sorry, I could not answer right now. Please try again shortly." },
      ]);
    }
    setBusy(false);
  };

  return (
    <>
      <button
        data-testid="ai-chat-open"
        onClick={() => setOpen((o) => !o)}
        aria-label="Vainor AI"
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-deepgreen text-gold-light border-2 border-gold px-4 py-3 rounded-full shadow-lg hover:bg-deepgreen-hover transition-colors"
      >
        <Sparkles size={18} />
        <span className="text-sm font-bold">{lang === "hi" ? "AI से पूछें" : "Ask AI"}</span>
      </button>

      {open && (
        <div
          data-testid="ai-chat-panel"
          className="fixed bottom-20 right-5 z-50 w-[92vw] max-w-sm bg-paper border-2 border-ink shadow-[8px_8px_0_#D4AF37] flex flex-col overflow-hidden"
        >
          <div className="bg-deepgreen text-paper px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles size={16} className="text-gold" />
              <span className="font-hindi text-lg">Vainor AI</span>
              <span className="font-mono text-[9px] uppercase tracking-[0.2em] text-gold/80">
                {lang === "hi" ? "ताज़ा ख़बरों से जुड़ा" : "linked to latest news"}
              </span>
            </div>
            <button data-testid="ai-chat-close" onClick={() => setOpen(false)} aria-label="close" className="text-paper/70 hover:text-gold transition-colors">
              <X size={18} />
            </button>
          </div>

          <div ref={listRef} className="h-80 overflow-y-auto px-4 py-4 space-y-3" data-testid="ai-chat-messages">
            {messages.length === 0 && (
              <p className="text-sm text-muted-foreground leading-relaxed">
                {lang === "hi"
                  ? "नमस्ते! आज की ख़बरों के बारे में कुछ भी पूछें — जैसे “आज की बड़ी ख़बरें क्या हैं?”"
                  : "Hi! Ask me anything about today's news — e.g. \"What are the top stories today?\""}
              </p>
            )}
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`ai-message-${i}`}
                className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-auto bg-deepgreen text-paper"
                    : "mr-auto bg-white border border-ink/15 text-inkgray"
                }`}
              >
                {m.text}
              </div>
            ))}
            {busy && (
              <div className="mr-auto bg-white border border-ink/15 px-3.5 py-2.5 text-sm text-muted-foreground">
                <span className="animate-pulse">…</span>
              </div>
            )}
          </div>

          <form onSubmit={send} className="border-t-2 border-ink flex" data-testid="ai-chat-form">
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={lang === "hi" ? "ख़बरों के बारे में पूछें…" : "Ask about the news…"}
              className="flex-1 bg-transparent px-4 py-3 text-sm outline-none placeholder:text-muted-foreground"
            />
            <button
              data-testid="ai-chat-send"
              type="submit"
              disabled={busy || !input.trim()}
              aria-label="send"
              className="bg-gold text-deepgreen px-4 hover:bg-gold-light transition-colors disabled:opacity-50"
            >
              <Send size={16} />
            </button>
          </form>
        </div>
      )}
    </>
  );
}
